import { usePublicCelebrities } from "@/features/celebrity";
import { useCelebrityStore } from "@/state/celebrity-store";
import { Check, Users } from "lucide-react";
import { cn } from "@pushup/ui";

interface CelebrityCirclesProps {
  onBrowseAll: () => void;
  /** Fires with the celebrity id when a circle is tapped — selects the opponent only. */
  onSelect: (celebrityId: string) => void;
}

/**
 * Left-edge opponent quick-select: up to 3 celebrity circles (the current
 * roster's first three) plus a fourth "browse all" circle that opens the full
 * picker. Tapping a circle only selects the opponent (highlighted with a ring
 * and check badge) — it never starts the workout itself.
 *
 * Renders for everyone, including signed-out users: the circles load the
 * public roster preview (id/name/image only). The caller is responsible for
 * sending signed-out taps to the login page.
 */
export function CelebrityCircles({ onBrowseAll, onSelect }: CelebrityCirclesProps) {
  const { data: celebrities, isLoading } = usePublicCelebrities();
  const selectedId = useCelebrityStore((s) => s.selectedCelebrityId);

  const roster = celebrities ?? [];
  const featured = roster.slice(0, 3);

  const handleSelect = (celebrityId: string) => {
    onSelect(celebrityId);
  };

  return (
    <div className="flex flex-col items-center gap-3">
      {featured.map((celebrity) => {
        const active = selectedId === celebrity.id;
        return (
          <button
            key={celebrity.id}
            type="button"
            onClick={() => handleSelect(celebrity.id)}
            className="group flex w-16 flex-col items-center gap-1"
            aria-pressed={active}
            aria-label={`Opponent ${celebrity.name}`}
          >
            <span
              className={cn(
                "relative flex size-14 items-center justify-center rounded-full border-2 bg-black/60 backdrop-blur-md transition-all",
                active
                  ? "border-orange-400 shadow-[0_0_14px_rgba(251,146,60,0.6)]"
                  : "border-white/20 group-hover:border-white/50",
              )}
            >
              <span className="flex size-full items-center justify-center overflow-hidden rounded-full">
                {celebrity.imageDataUrl ? (
                  <img
                    src={celebrity.imageDataUrl}
                    alt={celebrity.name}
                    className="size-full object-cover"
                  />
                ) : (
                  <span className="text-lg font-bold text-white/80">
                    {celebrity.name.charAt(0).toUpperCase()}
                  </span>
                )}
              </span>
              {active && (
                <span className="absolute -right-0.5 -top-0.5 z-10 flex size-4 items-center justify-center rounded-full bg-orange-400 text-black shadow-[0_0_8px_rgba(251,146,60,0.8)]">
                  <Check className="size-3" strokeWidth={3} />
                </span>
              )}
            </span>
            <span className="max-w-full truncate text-[9px] font-medium text-white/60">
              {celebrity.name}
            </span>
          </button>
        );
      })}

      <button
        type="button"
        onClick={onBrowseAll}
        className="group flex w-16 flex-col items-center gap-1"
        aria-label="Browse all opponents"
      >
        <span className="flex size-14 items-center justify-center rounded-full border-2 border-dashed border-white/30 bg-black/60 text-white/60 backdrop-blur-md transition-all group-hover:border-white/60 group-hover:text-white">
          <Users className="size-6" />
        </span>
        <span className="text-[9px] font-medium text-white/60">All</span>
      </button>

      {isLoading && roster.length === 0 && (
        <span className="text-[9px] text-white/40">Loading…</span>
      )}
    </div>
  );
}
